import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Paper
} from "@mui/material";
import {
  CloudSync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  People as ParticipantIcon,
  Assignment as TicketIcon,
  Add as TicketAddIcon,
  Update as TicketUpdateIcon,
  Warning as TicketErrorIcon
} from "@mui/icons-material";
import { i18n } from "../../../translate/i18n";
import toastUtils from "../../../utils/toastUtils";
import StandardModalAdv from "../../../components/shared/StandardModalAdv";
import { AuthContext } from "../../../context/Auth/AuthContext";
import api from "../../../services/api";

const SyncGroupsModal = ({ open, onClose, onComplete }) => {
  const { user, socket } = useContext(AuthContext);
  
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncResult, setSyncResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState("initial"); // initial, syncing, completed, error

  // ✅ SIMPLIFICADO: Escutar apenas o evento específico emitido pelo backend
  useEffect(() => {
    if (!open || !user?.companyId || !socket) return;

    console.log("[SyncGroupsModal] 🚀 Configurando listener para sync-groups na empresa:", user.companyId);

    const handleSyncGroups = (data) => {
      console.log("[SyncGroupsModal] 📡 Evento sync-groups recebido:", data);

      if (!data || !data.action) {
        console.warn("[SyncGroupsModal] ⚠️ Evento sem action:", data);
        return;
      }

      const { action, status, result, error, progress, userId } = data;
      
      // Verificar se o evento é para o usuário atual (se userId estiver presente)
      if (userId && userId !== user.id) {
        console.log(`[SyncGroupsModal] 📝 Evento ignorado - userId ${userId} !== ${user.id}`);
        return;
      }

      console.log("[SyncGroupsModal] 🎯 Processando action:", action);

      switch (action) {
        case "start":
          console.log("[SyncGroupsModal] ✅ Sincronização iniciada");
          setSyncing(true);
          setStep("syncing");
          setSyncStatus(status || "Iniciando sincronização...");
          setSyncProgress({ current: 0, total: 0 });
          setErrors([]);
          setSyncResult(null);
          break;
          
        case "progress":
          console.log("[SyncGroupsModal] 📊 Progresso:", progress, status);
          setSyncStatus(status || "Sincronizando...");
          
          if (progress && progress.current !== undefined && progress.total !== undefined) {
            setSyncProgress(progress);
          }
          break;
          
        case "complete":
          console.log("[SyncGroupsModal] 🎉 Sincronização concluída:", result);
          setSyncing(false);
          setStep("completed");
          setSyncResult(result);
          setSyncStatus(status || "Sincronização concluída!");
          
          if (result && (result.newGroups > 0 || result.updatedGroups > 0)) {
            const groupMessage = [];
            if (result.newGroups > 0) {
              groupMessage.push(`${result.newGroups} grupo(s) novo(s)`);
            }
            if (result.updatedGroups > 0) {
              groupMessage.push(`${result.updatedGroups} grupo(s) atualizado(s)`);
            }
            toastUtils.success(`${groupMessage.join(', ')}`, "Sincronização Concluída!");
          } else {
            toastUtils.success("Todos os grupos foram sincronizados com sucesso", "Sincronização Concluída!");
          }
          
          if (result && result.errors && result.errors.length > 0) {
            setErrors(result.errors);
          }
          
          // Auto-fechar modal após 3 segundos se não houver erros
          if (!result?.errors || result.errors.length === 0) {
            setTimeout(() => {
              console.log("[SyncGroupsModal] 🔄 Auto-fechando modal...");
              if (onComplete) onComplete();
              handleClose();
            }, 3000);
          }
          break;

        case "cancelled":
          console.log("[SyncGroupsModal] ⏹️ Sincronização cancelada");
          setSyncing(false);
          setStep("completed");
          setSyncStatus(status || "Sincronização interrompida pelo usuário");
          break;
          
        case "error":
          console.error("[SyncGroupsModal] ❌ Erro na sincronização:", error);
          setSyncing(false);
          setStep("error");
          setSyncStatus("Erro na sincronização");
          setErrors([error || "Erro desconhecido"]);
          toastUtils.error(error || "Erro desconhecido", "Erro na Sincronização");
          break;

        case "timeout":
          console.error("[SyncGroupsModal] ⏰ Timeout na sincronização:", error);
          setSyncing(false);
          setStep("error");
          setSyncStatus("Timeout na sincronização");
          setErrors([error || "Timeout na sincronização"]);
          toastUtils.warning(error || "Operação excedeu tempo limite", "Timeout na Sincronização");
          break;
          
        default:
          console.log("[SyncGroupsModal] ❓ Action não reconhecida:", action);
      }
    };

    // ✅ KISS: Escutar apenas o evento específico 'sync-groups'
    socket.on("sync-groups", handleSyncGroups);
    
    // ✅ Garantir que está conectado ao room correto
    socket.emit("joinCompany", user.companyId);

    return () => {
      console.log("[SyncGroupsModal] 🧹 Removendo listener sync-groups");
      socket.off("sync-groups", handleSyncGroups);
      socket.emit("leaveCompany", user.companyId);
    };
  }, [open, user?.companyId, user?.id, socket, onComplete]);

  // 🔧 CORRIGIDO: handleStartSync com timeout inteligente e recuperação
  const handleStartSync = async () => {
    try {
      setSyncing(true);
      setStep("syncing");
      setSyncStatus("Enviando solicitação de sincronização...");
      setErrors([]);
      setSyncResult(null);
      setSyncProgress({ current: 0, total: 0 });
      
      console.log("[SyncGroupsModal] 🚀 Iniciando sincronização via API...");
      
      // 🔧 Timeout progressivo: avisos em 30s, 60s e timeout final em 120s
      const warningTimeout1 = setTimeout(() => {
        console.warn("[SyncGroupsModal] ⚠️ Sincronização em andamento há 30 segundos...");
        setSyncStatus("Sincronização em andamento... Isso pode levar alguns minutos dependendo do número de grupos.");
      }, 30000);
      
      const warningTimeout2 = setTimeout(() => {
        console.warn("[SyncGroupsModal] ⚠️ Sincronização em andamento há 60 segundos...");
        setSyncStatus("Processando grupos... Aguarde, estamos sincronizando uma grande quantidade de dados.");
      }, 60000);
      
      const fallbackTimeout = setTimeout(() => {
        console.warn("[SyncGroupsModal] ❌ Timeout de 120 segundos atingido");
        setSyncing(false);
        setStep("error");
        setSyncStatus("Timeout na sincronização");
        setErrors([
          "A sincronização não respondeu em tempo hábil.", 
          "A operação pode estar executando em background.", 
          "Aguarde alguns minutos e verifique se novos grupos apareceram na lista."
        ]);
        toastUtils.error("Timeout na sincronização. A operação pode estar executando em background.");
      }, 120000);
      
      // 🔧 Salvar referências dos timeouts para limpeza posterior
      window.syncTimeouts = [warningTimeout1, warningTimeout2, fallbackTimeout];
      
      const response = await api.post("/groups/sync");
      console.log("[SyncGroupsModal] ✅ Resposta da API de sincronização:", response.data);
      
      // ✅ Limpar todos os timeouts se API respondeu
      if (window.syncTimeouts) {
        window.syncTimeouts.forEach(timeout => clearTimeout(timeout));
        delete window.syncTimeouts;
      }
      
      // Se API respondeu com dados imediatamente (sincronização muito rápida)
      if (response.data && (response.data.result || response.data.completed)) {
        console.log("[SyncGroupsModal] 🎉 Sincronização completada imediatamente");
        setSyncing(false);
        setStep("completed");
        setSyncResult(response.data.result || response.data);
        setSyncStatus("Sincronização concluída!");
        toastUtils.success("Todos os grupos foram sincronizados", "Sincronização Concluída!");
        
        setTimeout(() => {
          console.log("[SyncGroupsModal] 🔄 Auto-fechando modal após resposta imediata...");
          if (onComplete) onComplete();
          handleClose();
        }, 2000);
      }
      
    } catch (error) {
      console.error("[SyncGroupsModal] ❌ Erro ao iniciar sincronização:", error);
      
      // Limpar timeouts em caso de erro
      if (window.syncTimeouts) {
        window.syncTimeouts.forEach(timeout => clearTimeout(timeout));
        delete window.syncTimeouts;
      }
      
      toastUtils.error(error.response?.data?.message || error.message || "Erro desconhecido", "Erro ao Iniciar Sincronização");
      setSyncing(false);
      setStep("error");
      setErrors([error.response?.data?.message || error.message || "Erro desconhecido"]);
    }
  };

  const handleClose = () => {
    // Limpar timeouts ao fechar
    if (window.syncTimeouts) {
      window.syncTimeouts.forEach(timeout => clearTimeout(timeout));
      delete window.syncTimeouts;
    }
    
    // Resetar estado
    setSyncStatus("");
    setSyncProgress({ current: 0, total: 0 });
    setSyncResult(null);
    setErrors([]);
    setStep("initial");
    setSyncing(false);
    onClose();
  };

  const handleForceClose = () => {
    console.log("[SyncGroupsModal] 🚨 Forçando fechamento do modal");
    toastUtils.warning("A sincronização pode continuar em background", "Modal Fechado");
    handleClose();
  };

  const handleStopSync = async () => {
    try {
      setSyncStatus("Interrompendo sincronização no servidor...");
      await api.post("/groups/sync/cancel");
      toastUtils.info("Interrupção da sincronização solicitada...");
    } catch (err) {
      console.error("Erro ao cancelar sincronização:", err);
      toastUtils.error("Erro ao cancelar sincronização");
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    handleClose();
  };

  const renderProgress = () => {
    if (syncProgress.total > 0) {
      const percentage = Math.round((syncProgress.current / syncProgress.total) * 100);
      return (
        <Box sx={{ mt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="textSecondary">
              Progresso: {syncProgress.current} de {syncProgress.total} grupos
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {percentage}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={percentage} 
            sx={{ height: 8, borderRadius: 5 }}
          />
        </Box>
      );
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <LinearProgress sx={{ height: 8, borderRadius: 5 }} />
      </Box>
    );
  };

  const renderModalContent = () => {
    if (step === "initial") {
      return (
        <Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Esta operação irá sincronizar todos os grupos do WhatsApp com o sistema.
            O processo pode levar alguns minutos dependendo da quantidade de grupos.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>O que será feito:</strong><br />
              • Buscar todos os grupos das conexões WhatsApp ativas<br />
              • Criar/atualizar informações dos grupos no sistema<br />
              • Criar tickets automáticos para grupos onde você participa<br />
              • Identificar grupos onde você é administrador ou participante
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (step === "syncing") {
      return (
        <Box>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body1">
              {syncStatus}
            </Typography>
          </Box>
          
          {renderProgress()}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Por favor, aguarde. Não feche esta janela durante a sincronização.
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (step === "completed") {
      return (
        <Box>
          <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
            <CheckIcon color="success" sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6" color="success.main">
              {syncStatus}
            </Typography>
          </Box>

          {syncResult && (
            <List>
              <ListItem>
                <ListItemIcon>
                  <GroupIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{syncResult.totalGroups || 0} grupos encontrados</span>
                      <Chip label="TOTAL" size="small" color="primary" />
                    </Box>
                  }
                  secondary={`${syncResult.newGroups || 0} novos • ${syncResult.updatedGroups || 0} atualizados`}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <AdminIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{syncResult.adminGroups || 0} grupos como administrador</span>
                      <Chip label="Admin" size="small" color="success" />
                    </Box>
                  }
                  secondary="Você pode gerenciar todos os aspectos destes grupos"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <ParticipantIcon color="default" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{syncResult.participantGroups || 0} grupos como participante</span>
                      <Chip label="Participante" size="small" color="default" />
                    </Box>
                  }
                  secondary="Você pode extrair contatos destes grupos"
                />
              </ListItem>

              {(syncResult.ticketsCreated > 0 || syncResult.ticketsUpdated > 0 || syncResult.ticketsErrors > 0) && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Chip 
                      label="TICKETS GERADOS" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Divider>
                  
                  {syncResult.ticketsCreated > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <TicketAddIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{syncResult.ticketsCreated} tickets criados</span>
                            <Chip label="NOVOS" size="small" color="success" />
                          </Box>
                        }
                        secondary="Tickets automáticos para grupos onde você é participante/admin"
                      />
                    </ListItem>
                  )}
                  
                  {syncResult.ticketsUpdated > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <TicketUpdateIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{syncResult.ticketsUpdated} tickets atualizados</span>
                            <Chip label="REABERTOS" size="small" color="info" />
                          </Box>
                        }
                        secondary="Tickets existentes que foram reabertos e atribuídos a você"
                      />
                    </ListItem>
                  )}
                  
                  {syncResult.ticketsErrors > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <TicketErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{syncResult.ticketsErrors} erros em tickets</span>
                            <Chip label="ERRO" size="small" color="error" />
                          </Box>
                        }
                        secondary="Alguns tickets não puderam ser criados/atualizados"
                      />
                    </ListItem>
                  )}
                </>
              )}

              {(syncResult.ticketsCreated > 0 || syncResult.ticketsUpdated > 0) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <TicketIcon color="success" />
                    <Typography variant="subtitle2" color="success.main" fontWeight="bold">
                      TICKETS DISPONÍVEIS
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="success.dark">
                    {syncResult.ticketsCreated + syncResult.ticketsUpdated} ticket(s) de grupo(s) estão agora disponíveis na sua lista de atendimentos com status "Pendente" e já atribuídos a você.
                  </Typography>
                </Box>
              )}
            </List>
          )}

          {/* Mostrar erros se existirem */}
          {errors.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Alguns erros foram encontrados:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                {errors.slice(0, 5).map((error, index) => (
                  <Typography component="li" variant="body2" key={index}>
                    {error}
                  </Typography>
                ))}
                {errors.length > 5 && (
                  <Typography component="li" variant="body2" style={{ fontStyle: 'italic' }}>
                    ... e mais {errors.length - 5} erro(s)
                  </Typography>
                )}
              </Box>
            </Alert>
          )}
        </Box>
      );
    }

    if (step === "error") {
      return (
        <Box>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ErrorIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h6" color="error.main">
              {syncStatus}
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Ocorreu um erro durante a sincronização. Tente novamente em alguns minutos.
            </Typography>
          </Alert>

          {errors.length > 0 && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Detalhes do erro:
              </Typography>
              {errors.map((error, index) => (
                <Typography variant="body2" key={index} sx={{ mb: 0.5 }}>
                  • {error}
                </Typography>
              ))}
            </Paper>
          )}
        </Box>
      );
    }
  };

  // Definir ações do modal baseadas no estado
  const getModalActions = () => {
    if (step === "initial") {
      return [
        {
          label: "Cancelar",
          onClick: handleClose,
          variant: "outlined"
        },
        {
          label: "Iniciar Sincronização",
          onClick: handleStartSync,
          variant: "contained",
          startIcon: <SyncIcon />
        }
      ];
    }

    if (step === "syncing") {
      return [
        {
          label: "Parar Sincronização",
          onClick: handleStopSync,
          variant: "outlined",
          color: "error"
        },
        {
          label: "Continuar em Segundo Plano",
          onClick: handleForceClose,
          variant: "contained",
          color: "primary"
        }
      ];
    }

    if (step === "completed") {
      return [
        {
          label: "Concluir",
          onClick: handleComplete,
          variant: "contained",
          color: "success",
          startIcon: <CheckIcon />
        }
      ];
    }

    if (step === "error") {
      return [
        {
          label: "Fechar",
          onClick: handleClose,
          variant: "outlined"
        },
        {
          label: "Tentar Novamente",
          onClick: () => {
            setStep("initial");
            setErrors([]);
            setSyncStatus("");
            setSyncResult(null);
          },
          variant: "contained",
          startIcon: <SyncIcon />
        }
      ];
    }

    return [];
  };

  return (
    <StandardModalAdv
      open={open}
      onClose={handleClose}
      title="Sincronizar Grupos do WhatsApp"
      subtitle="Sincronize os grupos das suas conexões WhatsApp com o sistema"
      icon={SyncIcon}
      iconColor="#1976d2"
      maxWidth="md"
      fullWidth
      actions={getModalActions()}
      disableBackdropClick={syncing}
      disableEscapeKeyDown={syncing}
    >
      {renderModalContent()}
    </StandardModalAdv>
  );
};

export default SyncGroupsModal;